import { useMemo, useState } from "react";
import { allZones, browserZone, canonical, commonZones, findZones, type ZoneOption, zoneSummary } from "../lib/zones";
import { Sheet } from "./phone/Sheet";
import { usePhone } from "./phone/usePhone";

/**
 * The one time zone picker: a button that says the zone ("India · GMT+5:30"),
 * opening every zone in the world in a sheet, found by a city, a country or an
 * offset. The common zones come first, with this device's own on top. The
 * button is drawn as the app's dropdown, so it sits in any form beside one.
 */
export function TimeZonePicker({
  value,
  onChange,
  className,
  placeholder = "Pick a time zone",
  ariaLabel = "Time zone",
}: {
  value: string | null;
  onChange: (zone: string) => void;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searching = query.trim() !== "";

  const found = useMemo(() => (open && searching ? findZones(query) : []), [open, searching, query]);
  const common = useMemo(() => (open && !searching ? commonZones([value, browserZone()]) : []), [open, searching, value]);
  const all = useMemo(() => (open && !searching ? allZones() : []), [open, searching]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };
  const picked = value ? canonical(value) : null;
  const row = (o: ZoneOption, at: string) => (
    <button
      key={`${at}-${o.zone}`}
      type="button"
      className="ph-row"
      aria-pressed={picked === canonical(o.zone)}
      onClick={() => {
        onChange(o.zone);
        close();
      }}
    >
      <span className="m">
        <span className="t">{o.label}</span>
        <span className="s">{o.line}</span>
      </span>
      {picked === canonical(o.zone) ? (
        <span className="ph-tick">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 13l4 4 10-10" />
          </svg>
        </span>
      ) : null}
    </button>
  );

  return (
    <>
      <div className={className ? `fr-select ${className}` : "fr-select"}>
        <button
          type="button"
          className="fr-select__trigger"
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <span className={value ? "fr-select__value" : "fr-select__value is-placeholder"}>
            {value ? zoneSummary(value) : placeholder}
          </span>
          <svg viewBox="0 0 24 24" className="fr-select__chev" aria-hidden="true">
            <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <Sheet open={open} onClose={close} title="Time zone" tall className="tzp">
        <label className="ph-search tzp-search">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.5-3.5" />
          </svg>
          <input dir="auto"
            type="search"
            placeholder="Search a city or country"
            aria-label="Search time zones"
            autoComplete="off"
            autoCapitalize="none"
            enterKeyHint="search"
            // A phone keeps its keyboard down until the person asks for it.
            autoFocus={!phone}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        {searching ? (
          found.length ? (
            <div className="ph-grp">{found.map((o) => row(o, "found"))}</div>
          ) : (
            <p className="ph-note">No time zone matches “{query.trim()}”. Try a nearby big city.</p>
          )
        ) : (
          <>
            <section>
              <div className="ph-cap">Common</div>
              <div className="ph-grp">{common.map((o) => row(o, "common"))}</div>
            </section>
            <section>
              <div className="ph-cap">Every time zone</div>
              <div className="ph-grp">{all.map((o) => row(o, "all"))}</div>
            </section>
          </>
        )}
      </Sheet>
    </>
  );
}
